"use client"

import { useState, useEffect } from "react"
import { useGoogleReCaptcha } from "@/lib/recaptcha-client"
import { motion, AnimatePresence } from "framer-motion"
import { X, Sparkles, Loader2, Share2, CheckCircle2, Copy, ExternalLink } from "lucide-react"
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
} from "@/components/ui/drawer"
import { Textarea } from "@/components/ui/textarea"
import { Button } from "@/components/ui/button"
import { usePathname } from "next/navigation"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"
import Image from "next/image"
import { RequestQuoteButton } from "@/components/sections/RequestQuoteButton"

interface AnalysisResult {
  story: string
  suggestedServices: string[]
  keyFeatures: string[]
  estimatedComplexity: 'low' | 'medium' | 'high' | 'very-high'
  nextSteps: string[]
  shareToken: string | null
  shareUrl: string | null
}

const serviceLabels: Record<string, { tr: string; en: string }> = {
  'web-tasarim': { tr: 'Web Tasarım', en: 'Web Design' },
  'web-yazilim': { tr: 'Web Yazılım', en: 'Web Development' },
  'web-programlama': { tr: 'Web Yazılım', en: 'Web Development' },
  'ui-ux-tasarim': { tr: 'UI & UX Tasarım', en: 'UI & UX Design' },
  'mobil-uygulama': { tr: 'Mobil Uygulama', en: 'Mobile App' },
  'masaustu-uygulama': { tr: 'Masaüstü Uygulama', en: 'Desktop App' },
  'e-ticaret': { tr: 'E-ticaret', en: 'E-Commerce' },
  'shopify': { tr: 'Shopify', en: 'Shopify' },
  'woocommerce': { tr: 'WooCommerce', en: 'WooCommerce' },
  'otomasyon': { tr: 'Otomasyon', en: 'Automation' },
  'n8n-otomasyon': { tr: 'Otomasyon', en: 'Automation' },
  'ai-servisler': { tr: 'AI Servisler', en: 'AI Services' },
}

// Service to image mapping
const serviceImages: Record<string, string> = {
  'web-tasarim': '/web-design.jpg',
  'web-yazilim': '/web-programlama.jpg',
  'web-programlama': '/web-programlama.jpg',
  'ui-ux-tasarim': '/ui-ux.jpg',
  'mobil-uygulama': '/mobile-design.jpg',
  'masaustu-uygulama': '/masaustu-uygulama.jpg',
  'e-ticaret': '/e-commerce.jpg',
  'shopify': '/shopify.jpg',
  'woocommerce': '/woocommerce.jpg',
  'otomasyon': '/otomasyon.png',
  'n8n-otomasyon': '/otomasyon.png',
  'ai-servisler': '/ai.jpg',
}

interface DreamAssistantProps {
  locale?: Locale
}

export function DreamAssistant({ locale: propLocale }: DreamAssistantProps) {
  const pathname = usePathname()
  const [isOpen, setIsOpen] = useState(false)
  
  const getCurrentLocale = (): Locale => {
    // If locale is passed as prop, use it
    if (propLocale && isValidLocale(propLocale)) {
      return propLocale
    }
    
    // Otherwise, try to detect from pathname
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const currentLocale = getCurrentLocale()
  const t = getTranslations(currentLocale)
  
  // Listen for open event from header button
  useEffect(() => {
    const handleOpen = () => {
      setIsOpen(true)
    }
    
    if (typeof window !== 'undefined') {
      window.addEventListener('open-dream-assistant', handleOpen)
      return () => {
        window.removeEventListener('open-dream-assistant', handleOpen)
      }
    }
  }, [])
  
  // Fallback to Turkish if dreamAssistant translations are missing
  if (!t || !t.dreamAssistant) {
    const trTranslations = getTranslations('tr')
    if (trTranslations && trTranslations.dreamAssistant) {
      // Use Turkish translations as fallback
      const fallbackT = trTranslations
      // Continue with fallback translations
      return <DreamAssistantContent locale={currentLocale} t={fallbackT} isOpen={isOpen} setIsOpen={setIsOpen} />
    }
    console.error('DreamAssistant translations not loaded for locale:', currentLocale, 'Available keys:', t ? Object.keys(t) : 't is null')
    return null
  }
  
  return <DreamAssistantContent locale={currentLocale} t={t} isOpen={isOpen} setIsOpen={setIsOpen} />
}

function DreamAssistantContent({ locale: currentLocale, t, isOpen, setIsOpen }: { locale: Locale; t: any; isOpen: boolean; setIsOpen: (open: boolean) => void }) {
  const pathname = usePathname()
  const [dreamDescription, setDreamDescription] = useState("")
  const [analyzing, setAnalyzing] = useState(false)
  const [result, setResult] = useState<AnalysisResult | null>(null)
  const [error, setError] = useState("")
  const [shareCopied, setShareCopied] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleAnalyze = async () => {
    if (!dreamDescription.trim() || dreamDescription.trim().length < 10) {
      setError(currentLocale === 'tr' 
        ? 'Lütfen en az 10 karakter uzunluğunda bir açıklama girin.'
        : 'Please enter a description of at least 10 characters.')
      return
    }

    setError("")
    setAnalyzing(true)
    setResult(null)

    try {
      // Get reCAPTCHA token
      let recaptchaToken = ""
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("dream_analysis")
      }

      const response = await fetch('/api/ai/dream-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          dream_description: dreamDescription.trim(),
          locale: currentLocale,
          recaptchaToken,
        }),
      })

      const data = await response.json()

      if (!response.ok) {
        // Daha detaylı hata mesajı göster (development modunda)
        const errorMsg = data.error || t.dreamAssistant.error
        const details = data.details && process.env.NODE_ENV === 'development' 
          ? ` (${data.details})` 
          : ''
        setError(errorMsg + details)
        setAnalyzing(false)
        return
      }

      setResult({
        story: data.story,
        suggestedServices: data.suggestedServices || [],
        keyFeatures: data.keyFeatures || [],
        estimatedComplexity: data.estimatedComplexity || 'medium',
        nextSteps: data.nextSteps || [],
        shareToken: data.shareToken || null,
        shareUrl: data.shareUrl || null,
      })
    } catch (err: any) {
      setError(err.message || t.dreamAssistant.error)
    } finally {
      setAnalyzing(false)
    }
  }

  const handleShare = async () => {
    if (!result?.shareUrl) return

    try {
      await navigator.clipboard.writeText(result.shareUrl)
      setShareCopied(true)
      setTimeout(() => setShareCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleReset = () => {
    setDreamDescription("")
    setResult(null)
    setError("")
    setShareCopied(false)
  }

  return (
    <>
      {/* Drawer */}
      <Drawer open={isOpen} onOpenChange={setIsOpen} direction="right">
        <DrawerContent className="h-full w-full sm:max-w-lg">
          <DrawerHeader className="border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#034AA6]/10 rounded-lg">
                  <Sparkles className="h-6 w-6 text-[#034AA6]" />
                </div>
                <div>
                  <DrawerTitle className="text-2xl font-bold font-mono">{t.dreamAssistant.title}</DrawerTitle>
                  <DrawerDescription>
                    {t.dreamAssistant.description}
                  </DrawerDescription>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsOpen(false)
                  setTimeout(() => {
                    handleReset()
                  }, 300)
                }}
                className="rounded-md p-2 hover:bg-gray-100 transition-colors"
                aria-label={currentLocale === 'tr' ? 'Kapat' : 'Close'}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto p-6">
            {!result ? (
              // Input Form
              <div className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#034AA6]/10 rounded-lg">
                      <Sparkles className="h-5 w-5 text-[#034AA6]" />
                    </div>
                    <h2 className="text-xl font-bold font-mono">
                      {currentLocale === 'tr' ? 'Projenizi Anlatın' : 'Describe Your Project'}
                    </h2>
                  </div>
                  
                  <Textarea
                    placeholder={t.dreamAssistant.placeholder}
                    value={dreamDescription}
                    onChange={(e) => setDreamDescription(e.target.value)}
                    rows={10}
                    className="min-h-[200px] resize-none"
                    disabled={analyzing}
                  />
                  
                  <p className="text-sm text-muted-foreground">
                    {currentLocale === 'tr' 
                      ? `${dreamDescription.length} / 5000 karakter`
                      : `${dreamDescription.length} / 5000 characters`}
                  </p>
                </div>

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <Button
                  onClick={handleAnalyze}
                  disabled={analyzing || !dreamDescription.trim() || dreamDescription.trim().length < 10}
                  size="lg"
                  className="w-full bg-[#034AA6] hover:bg-[#034AA6]/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {analyzing ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {t.dreamAssistant.analyzing}
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-5 w-5" />
                      {t.dreamAssistant.submit}
                    </>
                  )}
                </Button>

                {analyzing && (
                  <div className="text-center space-y-2">
                    <p className="text-sm text-muted-foreground">{t.dreamAssistant.analyzingSubtext}</p>
                    <div className="flex justify-center gap-1">
                      <motion.div
                        className="w-2 h-2 bg-[#034AA6] rounded-full"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                      />
                      <motion.div
                        className="w-2 h-2 bg-[#034AA6] rounded-full"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                      />
                      <motion.div
                        className="w-2 h-2 bg-[#034AA6] rounded-full"
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                      />
                    </div>
                  </div>
                )}
              </div>
            ) : (
              // Results
              <div className="space-y-6">
                {/* Story with Example Image */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#034AA6]/10 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-[#034AA6]" />
                    </div>
                    <h2 className="text-xl font-bold font-mono">{t.dreamAssistant.storyTitle}</h2>
                  </div>
                  
                  {/* Example Image based on project type */}
                  {result.suggestedServices.length > 0 && (() => {
                    const primaryService = result.suggestedServices[0]
                    const imagePath = serviceImages[primaryService] || '/web-design.jpg'
                    return (
                      <div className="relative w-full h-48 sm:h-64 rounded-lg overflow-hidden border border-[#034AA6]/20 shadow-md">
                        <Image
                          src={imagePath}
                          alt={serviceLabels[primaryService]?.[currentLocale] || primaryService}
                          fill
                          className="object-cover"
                          sizes="(max-width: 768px) 100vw, 50vw"
                        />
                        {/* Gradient overlay for better text readability */}
                        <div className="absolute inset-0 bg-gradient-to-t from-[#034AA6]/60 via-[#034AA6]/30 to-transparent" />
                        {/* Service label overlay */}
                        <div className="absolute bottom-0 left-0 right-0 p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <Sparkles className="h-4 w-4 text-white" />
                            <p className="text-xs text-white/90 font-medium">
                              {currentLocale === 'tr' 
                                ? 'Örnek Görsel'
                                : 'Example Visual'}
                            </p>
                          </div>
                          <p className="text-sm text-white font-semibold">
                            {serviceLabels[primaryService]?.[currentLocale] || primaryService}
                          </p>
                        </div>
                      </div>
                    )
                  })()}
                  
                  <div className="bg-[#feffff] p-4 rounded-lg border border-[#034AA6]/20">
                    <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{result.story}</p>
                  </div>
                </div>

                {/* Suggested Services */}
                {result.suggestedServices.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#034AA6]/10 rounded-lg">
                        <CheckCircle2 className="h-5 w-5 text-[#034AA6]" />
                      </div>
                      <h2 className="text-xl font-bold font-mono">{t.dreamAssistant.servicesTitle}</h2>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {result.suggestedServices.map((service, idx) => (
                        <span
                          key={idx}
                          className="px-3 py-1.5 bg-[#034AA6]/10 text-[#034AA6] rounded-full text-sm font-medium"
                        >
                          {serviceLabels[service]?.[currentLocale] || service}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Key Features */}
                {result.keyFeatures.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#034AA6]/10 rounded-lg">
                        <CheckCircle2 className="h-5 w-5 text-[#034AA6]" />
                      </div>
                      <h2 className="text-xl font-bold font-mono">{t.dreamAssistant.featuresTitle}</h2>
                    </div>
                    <ul className="space-y-2">
                      {result.keyFeatures.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <span className="text-[#034AA6] mt-1">•</span>
                          <span className="text-slate-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Complexity */}
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-[#034AA6]/10 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-[#034AA6]" />
                    </div>
                    <h2 className="text-xl font-bold font-mono">{t.dreamAssistant.complexityTitle}</h2>
                  </div>
                  <div className="px-4 py-2 bg-[#feffff] rounded-lg border border-[#034AA6]/20">
                    <span className="text-[#034AA6] font-medium">
                      {t.dreamAssistant.complexity[result.estimatedComplexity]}
                    </span>
                  </div>
                </div>

                {/* Next Steps */}
                {result.nextSteps.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-[#034AA6]/10 rounded-lg">
                        <CheckCircle2 className="h-5 w-5 text-[#034AA6]" />
                      </div>
                      <h2 className="text-xl font-bold font-mono">{t.dreamAssistant.nextStepsTitle}</h2>
                    </div>
                    <ol className="space-y-2 list-decimal list-inside">
                      {result.nextSteps.map((step, idx) => (
                        <li key={idx} className="text-slate-700 pl-2">
                          {step}
                        </li>
                      ))}
                    </ol>
                  </div>
                )}

                {/* Actions */}
                <div className="pt-4 border-t space-y-3">
                  {result.shareUrl && (
                    <Button
                      onClick={handleShare}
                      variant="outline"
                      className="w-full"
                    >
                      {shareCopied ? (
                        <>
                          <CheckCircle2 className="mr-2 h-4 w-4" />
                          {t.dreamAssistant.shareSuccess}
                        </>
                      ) : (
                        <>
                          <Share2 className="mr-2 h-4 w-4" />
                          {t.dreamAssistant.share}
                        </>
                      )}
                    </Button>
                  )}
                  
                  <RequestQuoteButton shareToken={result.shareToken} locale={currentLocale} className="w-full" />

                  <Button
                    onClick={handleReset}
                    variant="ghost"
                    className="w-full"
                  >
                    {t.dreamAssistant.tryAgain}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  )
}

